import { ArrowLeft } from "lucide-react";
import moment from "moment";
import posthog from "posthog-js";
import Header from "components/header";
import BounceLoader from "components/loaders/bounce-loader";
import { CB_EVENTS, CB_CHANNELS } from "campaign-builder/analytics/cbAnalytics";
import { CAMPAIGN_SUBTYPES_ENUM } from "shared/constants";
import ROUTES, { EDDM_ROUTING } from "config/routing";
import { EDDM_NAVIGATION, READABLE_CAMPAIGN_INTERNAL_STATUSES } from "pages/constants";
import ConfirmationPopUp from "pages/flyer/flyerTypePage/components/ConfirmationPopUp";
import { useGetDataForPage } from "pages/flyer/flyerTypePage/useGetDataForPage";
import DesignSelection from "pages/creative/pageV2/DesignSelection";
import { useGetFlyerInfo } from "pages/flyer/flyerTypePage/useGetFlyerInfo";
import React, { memo, useEffect, useState, useCallback, useMemo } from "react";
import { EddmOadsSidebar } from "campaign-builder/EddmOadsSidebar";
import { Helmet } from "react-helmet";
import { useHistory } from "react-router";
import { generatePath } from "react-router-dom";
import { getRedirectedCampaigns } from "utils/getRedirectedCampaigns";
import { addBusinessDays } from "components/date-picker/utils";
import { DM_MIN_SELECTABLE_DATE } from "../../../constants";
import { useStore } from "../../../store";
import { StoreType } from "../../../store/types";
import { useEDDMCostsCalculation } from "Hooks/useEDDMCostsCalculation";
import { getDMPrintingCostByFlyer } from "shared/campaign";
import { calculateTaxAndTotalCost } from "Hooks/useEDDMCostsCalculation/utils";
import { getDMFlyersAmount } from "dm-campaign-data-hoc/utils";
import { toFixed2 } from "utils/formators";
import { getCurrencyCode } from "utils";
import useCalculating from "Hooks/useCalculating";
import { getCampaignCost } from "graphQL/query/campaign";
import { updateCampaignExtraData } from "graphQL/mutations/campaignExtraData";
import { updateCampaign as updateCampaignDB } from "../../../graphQL";
import { EDDMCampaignTrackingCard } from "pages/eddm/builder/components/EDDMCampaignTrackingCard";
import { CampaignSummaryCard } from "campaign-builder/summary/CampaignSummaryCard";
import { AudienceStatus } from "campaign-builder/domain/types";
import useDesignManagement from "pages/creative/pageV2/useDesignManagement";
import { DesignFormatChangeModal, DesignGalleryAddressBoxModal } from "pages/creative/pageV2/components";
import { buildAddressBoxSearch, getAddressBoxEntry } from "pages/creative/pageV2/utils/builderParams";
import { useFigmaAddressBoxDialog } from "pages/creative/pageV2/utils/useFigmaAddressBoxDialog";
import { seedImportOrientation } from "pages/creative/pageV2/utils/addressBoxOrientation";
import "campaign-builder/ui/theme.css";

const SelectFlyerTypePage: React.FC = () => {
  const flyerInfo = useGetFlyerInfo();
  const { recalculateEDDMCampaignCost } = useEDDMCostsCalculation();
  const { recalculateCampaignCost } = useCalculating();

  const {
    campaign: {
      id: campaignId,
      campaignName,
      isDM,
      flyerType,
      channel,
      subtype,
      startDate: savedStartDate,
      qrCodeLink: savedQrCodeLink,
      campaignComments: savedCampaignComments,
      isSubmitted,
      internalStatus,
    },
    client,
    user: { id: userId },
    country,
    city,
    timezone,
    costsCalculationData,
    updateCostsCalculationData,
    updateCampaign,
    updateErrors,
    distributionLocations,
  }: StoreType = useStore();
  const { id: clientId, name: clientName } = client;
  const history = useHistory();
  const { activateDesign, getDesign } = useDesignManagement({ clientId: clientId as string });

  // PAD-32157 — EDDM Design step milestone. This page is also reached by non-EDDM
  // flows, so gate on subtype === EDDM. Fires once on mount, in parallel to the
  // legacy design events (Completed Flyer Building, Uploaded Flyer Design, etc.)
  // which stay untouched during the transition. Cleanup: unregister channel on
  // unmount so `channel: 'EDDM'` doesn't leak into non-EDDM events emitted later.
  useEffect(() => {
    if (subtype !== CAMPAIGN_SUBTYPES_ENUM.EDDM) return;
    posthog.register({ channel: CB_CHANNELS.EDDM });
    if (campaignId && clientId) {
      posthog.capture(CB_EVENTS.DESIGN_REACHED, {
        campaign_id: campaignId,
        client_id: clientId,
      });
    }
    return () => {
      posthog.unregister("channel");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtype]);

  // EDDM-specific state
  const [isDesignSelectionOpen, setIsDesignSelectionOpen] = useState(true);
  const [isTrackingCardOpen, setIsTrackingCardOpen] = useState(false);
  // "none" is a marker value indicating user explicitly selected "No"
  const [hasQRCode, setHasQRCode] = useState<"yes" | "no" | "">(() => {
    if (savedQrCodeLink === "none") return "no";
    if (savedQrCodeLink) return "yes";
    return "";
  });
  const [qrCodeUrl, setQrCodeUrl] = useState(savedQrCodeLink && savedQrCodeLink !== "none" ? savedQrCodeLink : "");
  const [additionalComment, setAdditionalComment] = useState(
    Array.isArray(savedCampaignComments) ? savedCampaignComments.join("\n") : savedCampaignComments || ""
  );
  const [isSaving, setIsSaving] = useState(false);
  const [launchDateError, setLaunchDateError] = useState<string | null>(null);
  const [selectedDesign, setSelectedDesign] = useState<any>(null);
  const [hasLoadedTrackingData, setHasLoadedTrackingData] = useState(false);
  const [currentStep, setCurrentStep] = useState<"design" | "tracking">("design");
  const [showAddressBoxModal, setShowAddressBoxModal] = useState(false);
  const [designLaterSelected, setDesignLaterSelected] = useState(false);
  // Format-mismatch confirmation, mirrors the ADM Design page. When a
  // user picks a design whose `format` differs from the campaign's
  // current `flyerType`, we stash the pick here, surface the
  // DesignFormatChangeModal, and only run the real activation if they
  // confirm. Cancel asks DesignSelection to deselect its card AND
  // roll back the Zustand `flyerType` that the child optimistically
  // wrote on click. Only gated on EDDM at the moment — the H2H/D2D
  // path through this same page doesn't need a cost-impact warning.
  const [pendingFormatChangeDesign, setPendingFormatChangeDesign] = useState<any>(null);
  const [formatBeforeAttempt, setFormatBeforeAttempt] = useState<string | undefined>(undefined);
  const [galleryDeselectSignal, setGalleryDeselectSignal] = useState(0);

  // EDDM "what-if" cost preview for the format-change modal. Mirrors
  // `useEDDMCostsCalculation.recalculateEDDMCampaignCost` but operates
  // on the PENDING design's format and never writes back to the store
  // (no `updateCostsCalculationData`). Lets the modal show the user
  // what their total would change to BEFORE they commit to the
  // format swap. Only computes when the pending design + cost
  // dependencies are available; returns `undefined` otherwise so the
  // modal falls back to its generic copy.
  const pendingFormatTotal = useMemo<number | undefined>(() => {
    const pendingFormat = pendingFormatChangeDesign?.format;
    if (!pendingFormat) return undefined;
    const printingCosts = costsCalculationData?.printingCosts;
    if (!printingCosts) return undefined;
    const countryTaxRate = country?.countryDefaultSettings?.countryTaxRate || 0;
    const flyersAmount =
      distributionLocations && distributionLocations.length ? getDMFlyersAmount(distributionLocations) : 0;
    const costPerFlyer = getDMPrintingCostByFlyer({
      printingCosts,
      flyerType: pendingFormat,
      flyersAmount,
    });
    const subtotal = flyersAmount * costPerFlyer;
    const fixed2Subtotal = toFixed2(subtotal);
    const amountOff = costsCalculationData?.detailedCost?.amountOff || 0;
    const { totalCost } = calculateTaxAndTotalCost({
      countryTaxRate,
      subtotal: fixed2Subtotal,
      amountOff,
    });
    return toFixed2(totalCost);
  }, [pendingFormatChangeDesign?.format, costsCalculationData, country, distributionLocations]);

  // Current campaign total from the live cost calculation. Reads
  // straight from the store-computed `detailedCost.totalCost` so the
  // delta in the modal matches whatever number the rest of the page
  // is displaying.
  const currentEddmTotal = costsCalculationData?.detailedCost?.totalCost;

  // Sync QR code and comments from store when data loads (fixes refresh issue)
  useEffect(() => {
    if (!hasLoadedTrackingData && campaignId) {
      if (savedQrCodeLink === "none") {
        // User explicitly selected "No"
        setHasQRCode("no");
        setQrCodeUrl("");
      } else if (savedQrCodeLink) {
        // User selected "Yes" with URL
        setHasQRCode("yes");
        setQrCodeUrl(savedQrCodeLink);
      }
      if (savedCampaignComments) {
        const comments = Array.isArray(savedCampaignComments)
          ? savedCampaignComments.join("\n")
          : savedCampaignComments;
        setAdditionalComment(comments);
      }
      setHasLoadedTrackingData(true);
    }
  }, [savedQrCodeLink, savedCampaignComments, campaignId, hasLoadedTrackingData]);

  // Sync design from flyerInfo on page load only (similar to ADM)
  // Use a ref to track if we've done the initial sync to prevent re-syncing
  const hasInitializedDesign = React.useRef(false);

  useEffect(() => {
    // Only sync once on initial load, not on subsequent updates
    if (!hasInitializedDesign.current && flyerInfo?.flyerInfo?.activeRoomId && !flyerInfo.isLoading) {
      hasInitializedDesign.current = true;

      // Fetch the full design data including addressBox (but NOT format - that's controlled by FormatSelector)
      const loadDesignData = async () => {
        try {
          const designData = await getDesign(flyerInfo?.flyerInfo?.activeRoomId || "");

          setSelectedDesign({
            id: flyerInfo?.flyerInfo?.activeRoomId || "",
            format: flyerType, // Use current store format, not design's format
            addressBox: designData.addressBox || false,
            isUploaded: designData.isUploaded,
            s3Link: designData.s3Link,
            hasHtmlContent: designData.hasHtmlContent,
          });

          // Don't update flyerType here - it's controlled by FormatSelector
          // The store already has the correct format from user's selection
        } catch (error) {
          console.error("Failed to load design data:", error);
          // Fallback to basic data if fetch fails
          setSelectedDesign({
            id: flyerInfo?.flyerInfo?.activeRoomId || "",
            format: flyerType,
          });
        }
      };

      loadDesignData();

      // If design already exists, start at tracking step (like ADM)
      setCurrentStep("tracking");
      setIsDesignSelectionOpen(false);
      setIsTrackingCardOpen(true);
    }
  }, [flyerInfo?.flyerInfo?.activeRoomId, flyerInfo.isLoading, flyerType, getDesign]);

  // Refresh design data when returning from builder to get updated addressBox
  const lastRefreshTimestamp = React.useRef<number>(0);

  useEffect(() => {
    const refreshDesignData = async () => {
      // Prevent excessive refreshes - only refresh once every 2 seconds
      const now = Date.now();
      if (now - lastRefreshTimestamp.current < 2000) {
        return;
      }

      if (selectedDesign?.id && !flyerInfo.isLoading) {
        lastRefreshTimestamp.current = now;
        try {
          const designData = await getDesign(selectedDesign.id);

          // Only update addressBox and hasHtmlContent, not format
          // Format is controlled by the FormatSelector dropdown
          setSelectedDesign((prev: any) => ({
            ...prev,
            addressBox: designData.addressBox || false,
            hasHtmlContent: designData.hasHtmlContent,
          }));
        } catch (error) {
          console.error("❌ [refreshDesignData] Failed to refresh design data:", error);
        }
      }
    };

    // Refresh on mount and when dependencies change
    if (selectedDesign?.id && !flyerInfo.isLoading) {
      refreshDesignData();
    }

    // Add listener to refresh when page becomes visible (when returning from builder)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && selectedDesign?.id) {
        refreshDesignData();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [flyerInfo.isLoading, getDesign, selectedDesign?.id]);

  // Parse saved start date
  const startDate = useMemo(() => {
    if (!savedStartDate) return null;
    const date = new Date(savedStartDate);
    return isNaN(date.getTime()) ? null : date;
  }, [savedStartDate]);

  // Handle start date change
  const handleStartDateChange = useCallback(
    async (newDate: Date | null) => {
      if (!newDate) {
        setLaunchDateError("Please select a date");
        return;
      }

      const minDate = addBusinessDays(new Date(), DM_MIN_SELECTABLE_DATE, timezone);
      const newMomentDate = moment(newDate).format("DD/MM/YYYY");
      const minMomentDate = moment(minDate).format("DD/MM/YYYY");
      const daysDiff = moment(newMomentDate, "DD/MM/YYYY", true).diff(
        moment(minMomentDate, "DD/MM/YYYY", true),
        "days"
      );

      if (daysDiff < 0) {
        setLaunchDateError("Select a different date");
        return;
      }

      setLaunchDateError(null);
      // Create date at midnight in local timezone, then convert to UTC
      // This preserves the selected date regardless of timezone
      const year = newDate.getFullYear();
      const month = newDate.getMonth();
      const day = newDate.getDate();
      const localMidnight = new Date(year, month, day, 0, 0, 0, 0);
      const convertedDate = localMidnight.toISOString();

      updateCampaign({ startDate: convertedDate });

      // Update both Campaign table and CampaignExtraData table
      await Promise.all([
        updateCampaignDB({
          campaignId,
          startDate: new Date(convertedDate),
        }),
        updateCampaignExtraData({
          campaignId,
          startDate: new Date(convertedDate),
        }),
      ]);
    },
    [campaignId, timezone, updateCampaign]
  );

  // Check if design is selected
  const hasDesign = flyerInfo?.flyerInfo?.isFlyerUploaded || !!selectedDesign?.id;

  // Check if next button should be enabled for EDDM (depends on current step)
  const isEDDMNextEnabled = useMemo(() => {
    if (!isDM) return true;

    if (currentStep === "design") {
      // Design step: only need design selected
      return hasDesign;
    } else {
      // Tracking step: need QR setting and launch date
      const hasDate = !!startDate;
      const qrValid = hasQRCode === "no" || (hasQRCode === "yes" && !!qrCodeUrl);
      return hasDate && qrValid;
    }
  }, [isDM, currentStep, hasDesign, startDate, hasQRCode, qrCodeUrl]);

  // Sync currentStep with Design Selection open/close state
  useEffect(() => {
    if (isDM && isDesignSelectionOpen) {
      // If user opens Design Selection, go back to "design" step
      setCurrentStep("design");
    }
  }, [isDM, isDesignSelectionOpen]);

  // Get button text based on current step
  const eddmButtonText = currentStep === "design" ? "Continue to Tracking" : "Continue to Submit";

  // Handle EDDM next button click
  const handleEDDMNext = useCallback(() => {
    if (currentStep === "design") {
      // Validate addressBox before moving to tracking
      if (selectedDesign && !selectedDesign.addressBox) {
        setShowAddressBoxModal(true);
        return;
      }

      // Move to tracking step - collapse design, open tracking (like ADM)
      setCurrentStep("tracking");
      setIsDesignSelectionOpen(false);
      setIsTrackingCardOpen(true);
    } else {
      // Double-check addressBox before going to submit
      if (selectedDesign && !selectedDesign.addressBox) {
        setShowAddressBoxModal(true);
        return;
      }

      // Go to submit page
      history.push(generatePath(EDDM_ROUTING.EDDM_SUBMIT, { campaignId, clientId }));
    }
  }, [currentStep, history, campaignId, clientId, selectedDesign]);

  // Handle "Design later" button click - allows user to skip design and continue to tracking
  const handleDesignLater = useCallback(() => {
    // Mark that user chose to design later (skips design validation)
    setDesignLaterSelected(true);
    updateErrors({ notExistDesignFile: true });

    // Move to tracking step - collapse design, open tracking (same as Continue to Tracking)
    setCurrentStep("tracking");
    setIsDesignSelectionOpen(false);
    setIsTrackingCardOpen(true);
  }, [updateErrors]);

  // Real activation — applies the picked design, persists to the
  // backend, propagates the format to the campaign state. Split out
  // from `handleDesignActivate` so the guard below can defer this
  // until the user confirms the format-change modal (when applicable).
  const performDesignActivation = useCallback(
    async (design: any) => {
      // Store the full design object including addressBox and format properties
      setSelectedDesign(design);

      // Reset "design later" state since user is now selecting a design
      if (designLaterSelected) {
        setDesignLaterSelected(false);
        updateErrors({ notExistDesignFile: false });
      }

      // Update campaign flyerType to match the selected design's format
      if (design?.format) {
        updateCampaign({ flyerType: design.format });
      }

      if (design?.id && campaignId) {
        try {
          // Activate the design in the backend
          await activateDesign(campaignId, design.id);
          // Update lastActiveStep to mark design as selected
          await updateCampaignExtraData({ campaignId, lastActiveStep: "design" });
          updateCampaign({ lastActiveStep: "design" });
        } catch (error) {
          console.error("Failed to activate design:", error);
        }
      }
    },
    [campaignId, activateDesign, updateCampaign, designLaterSelected, updateErrors]
  );

  // Guarded activation. For EDDM campaigns, if the picked design's
  // format differs from the campaign's current `flyerType`, defer
  // activation and surface the DesignFormatChangeModal so the user
  // explicitly opts into the format (and cost) change. For all other
  // subtypes this is a straight pass-through.
  //
  // Important: by the time this handler runs, `DesignSelection`'s
  // `onDesignSelect` has already optimistically written the picked
  // design's format into Zustand `flyerType` (see DesignSelection.tsx
  // line ~318). The EDDMHeader has a `useEffect([flyerType])` that
  // immediately calls `recalculateEDDMCampaignCost`, so the campaign
  // total flashes to the new amount BEFORE the user sees the modal.
  // We undo that here by writing the old `flyerType` back in the same
  // render cycle — React batches the two store writes so the cost
  // recalc effect never fires (deps array sees no net change).
  // `flyerType` in this closure is the OLD value (captured before
  // DesignSelection's update), so it's safe to use as the rollback
  // target.
  const handleDesignActivate = useCallback(
    async (design: any) => {
      const isEddm = subtype === CAMPAIGN_SUBTYPES_ENUM.EDDM;
      const hasMismatch = design?.format && flyerType && design.format !== flyerType;
      if (isEddm && hasMismatch) {
        // Roll back the optimistic Zustand write so the EDDMHeader's
        // cost recalc effect doesn't fire. The visible total stays at
        // the current value until the user explicitly confirms via
        // `handleConfirmFormatChange`.
        updateCampaign({ flyerType });
        setFormatBeforeAttempt(flyerType as string);
        setPendingFormatChangeDesign(design);
        return;
      }
      await performDesignActivation(design);
    },
    [subtype, flyerType, updateCampaign, performDesignActivation]
  );

  const handleConfirmFormatChange = useCallback(async () => {
    const pending = pendingFormatChangeDesign;
    setPendingFormatChangeDesign(null);
    setFormatBeforeAttempt(undefined);
    if (pending) {
      await performDesignActivation(pending);
    }
  }, [pendingFormatChangeDesign, performDesignActivation]);

  const handleCancelFormatChange = useCallback(() => {
    setPendingFormatChangeDesign(null);
    // Tell DesignSelection to deselect the card AND roll back the
    // Zustand `flyerType` that the child optimistically wrote on
    // click (see DesignSelection's `onDesignSelect`). Same signal +
    // restore-value pattern the ADM Design page uses.
    setGalleryDeselectSignal((c) => c + 1);
  }, []);

  // Handle campaign name change with validation and save
  const handleCampaignNameChange = useCallback(
    async (newName: string): Promise<{ success: boolean; error?: string }> => {
      const trimmedName = newName.trim();
      const noSpaceName = newName.replace(/\s/g, "");

      if (!trimmedName) {
        return { success: false, error: "Campaign name is required" };
      }

      if (noSpaceName.length < 4) {
        return { success: false, error: "Campaign name is too short (minimum 4 characters)" };
      }

      if (trimmedName.length > 46) {
        return { success: false, error: "Campaign name should not exceed 46 characters" };
      }

      const { isValidCampaignName } = await import("components/audience-form/components/campaign-location/utils");
      const { REGEX_FOR_CAMPAIGN_NAME } = await import("../../../constants");

      if (REGEX_FOR_CAMPAIGN_NAME.test(trimmedName)) {
        return { success: false, error: "Campaign name contains invalid characters" };
      }

      const isValid = await isValidCampaignName({
        campaignName: trimmedName,
        clientId,
        campaignId,
      });

      if (!isValid) {
        return { success: false, error: "Duplicated campaign name. Please choose another." };
      }

      updateCampaign({ campaignName: trimmedName });
      await updateCampaignDB({
        campaignId,
        name: trimmedName,
      });

      return { success: true };
    },
    [campaignId, clientId, updateCampaign]
  );

  const openAddressBoxBuilder = useCallback(
    async (designId: string, designFormat: string | undefined, fileLink: string | null) => {
      // Navigate to Flyer Builder with the design
      const flyerBuilderPath = generatePath(ROUTES.FLYER_BUILDER, {
        clientId,
        campaignId,
        format: designFormat || flyerType,
      });

      // An uploaded PDF not yet edited in the builder, or a prepared Figma rendition, is imported automatically
      const orientation = await seedImportOrientation(fileLink, designId, `${campaignId}`);
      const fullPath = `${flyerBuilderPath}${buildAddressBoxSearch(designId, fileLink, orientation)}`;

      setTimeout(() => {
        history.push(fullPath);
      }, 100);
    },
    [clientId, campaignId, flyerType, history]
  );

  // The copy (or flattened original) becomes this campaign's design, so it is the one that gets the address box.
  const { openFigmaAddressBoxDialog, figmaAddressBoxDialog } = useFigmaAddressBoxDialog({
    onPrepared: (prepared, design) => {
      void (async () => {
        try {
          if (prepared.designId !== design.id) {
            await activateDesign(campaignId, prepared.designId);
            setSelectedDesign({
              ...design,
              id: prepared.designId,
              designSource: "upload",
              isUploaded: true,
              s3Link: prepared.fileLink,
              hasHtmlContent: false,
              addressBox: false,
            });
          }
          await openAddressBoxBuilder(prepared.designId, design.format ?? undefined, prepared.fileLink);
        } catch (error) {
          console.error(`[AddAddressBox] Error activating copy ${prepared.designId}:`, error);
        }
      })();
    },
  });

  // Handle adding address box to design
  const handleAddAddressBoxToDesign = useCallback(async () => {
    setShowAddressBoxModal(false);

    if (!selectedDesign) {
      console.error("[AddAddressBox] Missing selectedDesign");
      return;
    }

    try {
      // Always fetch the latest design data to ensure we have the correct format
      // This is important when the user changes the format dropdown after upload
      let designFormat = selectedDesign.format;
      let freshDesignData: any = null;

      if (selectedDesign.id) {
        try {
          freshDesignData = await getDesign(selectedDesign.id);
          designFormat = freshDesignData.format;
        } catch (error) {
          // Fallback to selectedDesign.format or flyerType if fetch fails
          designFormat = selectedDesign.format || flyerType;
        }
      }

      // activateDesign marks a Figma design as uploaded, so its source decides the path first
      const designSource = freshDesignData?.designSource ?? selectedDesign.designSource;
      const entry = getAddressBoxEntry({ ...selectedDesign, designSource });
      if (entry.kind === "needsFigmaChoice") {
        void openFigmaAddressBoxDialog({
          ...selectedDesign,
          format: designFormat || flyerType,
          hasFigmaPlaceholders: freshDesignData?.hasFigmaPlaceholders ?? selectedDesign.hasFigmaPlaceholders,
          usedByCampaign: freshDesignData?.usedByCampaign ?? selectedDesign.usedByCampaign,
        });
        return;
      }

      await openAddressBoxBuilder(selectedDesign.id, designFormat, entry.kind === "open" ? entry.fileLink : null);
    } catch (error) {
      console.error("[AddAddressBox] Error navigating:", error);
    }
  }, [selectedDesign, flyerType, getDesign, openFigmaAddressBoxDialog, openAddressBoxBuilder]);

  const handleCloseAddressBoxModal = useCallback(() => {
    setShowAddressBoxModal(false);
    if (isDM) {
      setIsDesignSelectionOpen(true);
      setTimeout(() => {
        const designSelectionElement =
          document.querySelector('[data-testid="design-selection"]') ||
          document.querySelector(".design-selection-container");
        if (designSelectionElement) {
          designSelectionElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);
    }
  }, [isDM]);

  const readyToUpload = getRedirectedCampaigns(campaignId);

  const { buttons, isConfirmationModalOpen, setIsConfirmationModalOpen } = useGetDataForPage(flyerInfo.pageState);

  const headerActions = {
    BACK: {
      action: async () => {
        if (isDM) {
          history.push(generatePath(EDDM_ROUTING.EDDM_CAMPAIGN_SEGMENTS, { campaignId, clientId }));
        } else {
          history.push(generatePath(ROUTES.CAMPAIGN_BUILDER, { campaignId, clientId }));
        }
      },
    },
  };

  useEffect(() => {
    // Determine campaign type
    const isDirectMail = isDM || subtype === CAMPAIGN_SUBTYPES_ENUM.EDDM || channel === "DM";

    try {
      const getDetailedCost = async () => {
        const detailedCost = await getCampaignCost({
          id: campaignId,
          cityId: city?.id,
        });
        console.log("detailedCost", detailedCost);

        if (isDirectMail) {
          // For DM/EDDM campaigns: Allow cost structure overwrite
          updateCostsCalculationData({
            detailedCost,
          });
          // Recalculate costs after getting detailed cost from backend to avoid race condition
          recalculateEDDMCampaignCost();
        } else {
          // For D2D campaigns: Don't overwrite cost structure to prevent corruption
          console.log("Skipping detailedCost overwrite for D2D campaign in flyerTypePage");
          // Trigger proper D2D cost recalculation instead
          recalculateCampaignCost();
        }
      };
      getDetailedCost();
    } catch (error) {
      console.log("error", error);
    }
  }, [flyerInfo.flyerInfo?.activeRoomId, flyerType]);

  const flyerBody = flyerInfo.isLoading ? (
    <div className="flex min-h-0 flex-1 items-center justify-center">
      <BounceLoader />
    </div>
  ) : (
    // EDDM Layout - Same structure as ADM Builder
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col bg-page">
      {/* PAD-32921: fluid and centred, like the ADM shell's max-width container. A fixed
          1080px width could not shrink below it (the Copilot panel pushes the page by its
          width), and justify-self never centred it inside this flex column. */}
      <div className="mx-auto flex min-h-0 w-full max-w-[1080px] flex-1 flex-col gap-6 pt-7">
        {/* Title Section - matches ADM */}
        <div className="flex flex-col items-start gap-3 self-stretch pl-4 pt-6">
          <div
            className="flex cursor-pointer items-center gap-2.5 text-base font-medium text-muted-foreground"
            onClick={() => history.push(generatePath(EDDM_ROUTING.EDDM_CAMPAIGN_SEGMENTS, { campaignId, clientId }))}
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Segments
          </div>
          <h1 className="m-0 text-2xl font-semibold text-[#222336] dark:text-foreground">Now, select your design</h1>
        </div>
        {/* Grid Layout - matches ADM: flexible content + fixed 360px summary */}
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-6 overflow-y-auto lg:grid-cols-[1fr_360px] lg:grid-rows-[minmax(0,1fr)] lg:overflow-visible">
          {/* Main content column - Design + Tracking */}
          <div className="min-h-0 min-w-0 overflow-y-auto">
            <div className="space-y-4">
              <DesignSelection
                isOpen={isDesignSelectionOpen}
                setIsOpen={setIsDesignSelectionOpen}
                showToggle={true}
                hasDesignSelected={!!flyerInfo?.flyerInfo?.isFlyerUploaded || !!selectedDesign?.id}
                isNextEnabled={isEDDMNextEnabled}
                onActivateDesign={handleDesignActivate}
                deselectSignal={galleryDeselectSignal}
                formatToRestoreOnDeselect={formatBeforeAttempt}
              />
              <EDDMCampaignTrackingCard
                campaignId={campaignId}
                isOpen={isTrackingCardOpen}
                setIsOpen={setIsTrackingCardOpen}
                isReadOnly={isSubmitted}
                isFileUploaded={!!flyerInfo?.flyerInfo?.isFlyerUploaded || !!selectedDesign?.id}
                hasQRCode={hasQRCode}
                setHasQRCode={setHasQRCode}
                qrCodeUrl={qrCodeUrl}
                setQrCodeUrl={setQrCodeUrl}
                additionalComment={additionalComment}
                setAdditionalComment={setAdditionalComment}
                setIsSaving={setIsSaving}
              />
            </div>
          </div>
          {/* Summary card column */}
          <div className="min-h-0 min-w-0 lg:overflow-y-auto">
            <div className="flex flex-col gap-4">
              <CampaignSummaryCard
                campaign={{
                  campaignName: campaignName,
                  campaignStartDate: startDate ?? undefined,
                  format: flyerType || "-",
                }}
                targeting={
                  city?.addresses?.map((address, index) => ({
                    id: parseInt(address.id),
                    name: `${address.value} ${index + 1}`,
                    csvUrl: "",
                    clientId: parseInt(clientId),
                    createdAt: "",
                    updatedAt: "",
                    deletedAt: "",
                    metadata: {
                      fileUrl: "",
                      headers: [],
                      firstRow: [],
                      headersMap: {},
                      rowsAmount: 0,
                    },
                    status: AudienceStatus.READY,
                  })) || []
                }
                flyerQuote={
                  costsCalculationData?.detailedCost
                    ? {
                        flyersAmount: costsCalculationData?.flyersAmount || 0,
                        discounts: [],
                        totalCost: costsCalculationData.detailedCost.totalCost || 0,
                      }
                    : null
                }
                onClickNext={handleEDDMNext}
                buttonText={eddmButtonText}
                isNextEnabled={isEDDMNextEnabled}
                isLoading={isSaving}
                isReadOnly={isSubmitted}
                internalStatus={
                  internalStatus
                    ? READABLE_CAMPAIGN_INTERNAL_STATUSES[
                        internalStatus as keyof typeof READABLE_CAMPAIGN_INTERNAL_STATUSES
                      ]
                    : undefined
                }
                // EDDM-specific props for date picker
                isEddm={true}
                onStartDateChange={handleStartDateChange}
                launchDateError={launchDateError}
                timezone={timezone}
                isSubmitted={isSubmitted}
                // Campaign name editing
                onCampaignNameChange={handleCampaignNameChange}
                // Design later button - only show on design step
                onClickSecondaryButton={currentStep === "design" ? handleDesignLater : undefined}
                secondaryButtonText={currentStep === "design" ? "Design later" : undefined}
              />
            </div>
          </div>
        </div>
      </div>
      <ConfirmationPopUp isOpen={isConfirmationModalOpen} onClose={() => setIsConfirmationModalOpen(false)} />

      {/* Address Box Modal */}
      <DesignGalleryAddressBoxModal
        open={showAddressBoxModal}
        onClose={handleCloseAddressBoxModal}
        onAddAddressBox={handleAddAddressBoxToDesign}
      />
      {figmaAddressBoxDialog}
      {/* Format-change warning. Fires for EDDM campaigns when
                the picked design's format differs from the campaign's
                current `flyerType`. Cost preview is computed inline
                via `pendingFormatTotal` — mirrors the EDDM cost
                pipeline (flyersAmount × costPerFlyer + tax) without
                writing to the store, so the modal can show the new
                total + delta before the user commits. */}
      <DesignFormatChangeModal
        open={!!pendingFormatChangeDesign}
        onCancel={handleCancelFormatChange}
        onConfirm={handleConfirmFormatChange}
        newTotal={pendingFormatTotal}
        currentTotal={currentEddmTotal}
        currency={country ? getCurrencyCode(country) : "USD"}
      />
    </div>
  );

  return (
    <div className="cb-v2-root flex h-screen w-full overflow-hidden">
      <Helmet>
        <title>Flyer</title>
      </Helmet>
      <EddmOadsSidebar country={country} client={client} />
      <main className="flex h-screen min-w-0 flex-1 flex-col overflow-hidden">
        <Header tabs={EDDM_NAVIGATION} activeTabIndex={2} headerActions={headerActions} isDM={isDM} />
        {flyerBody}
      </main>
    </div>
  );
};

export default memo(SelectFlyerTypePage);
